import {
  createFileRoute,
  stripSearchParams,
  useNavigate,
  useRouter,
} from '@tanstack/react-router'
import { fallback, zodValidator } from '@tanstack/zod-adapter'
import { useRef, useState } from 'react'
import { useEffectOnce } from 'react-use'
import { toast } from 'sonner'
import { z } from 'zod'

import { queryClient } from '@/api/api'
import { assignTaskToUserOptions } from '@/api/codegen/@tanstack/react-query.gen'
import { useAssignTaskToUserQuery } from '@/api/domain/task/task'
import { TaskAutoReleasedModal } from '@/components/modals/taskAutoReleasedModal/TaskAutoReleasedModal'
import { NoTaskAvailable } from '@/components/noTaskAvailable/NoTaskAvailable'
import { Loading } from '@/components/ui/Loading'
import { useSidebar } from '@/components/ui/Sidebar'
import { NOT_FOUND_ERROR } from '@/constants/error.constants'
import { SidebarKeyMap } from '@/constants/sidebar.constants'
import { SolutionPage, hasSolution } from '@/modules/client-relo'
import { TaskBasicPage } from '@/pages/task-basic/TaskBasic.page'
import { TaskVideoAnnotationPage } from '@/pages/task-objectTracking/TaskVideoAnnotation.page'
// eslint-disable-next-line project-structure/independent-modules
import { resetAllVideoAnnotationStores } from '@/pages/task-objectTracking/stores/videoAnnotationStores.utils'

const REFRESH_COOLDOWN_MS = 3000

const TaskPageContainer = () => {
  const { setOpen } = useSidebar(SidebarKeyMap.MAIN)
  const navigate = useNavigate()
  const router = useRouter()
  const { returnTo } = Route.useSearch()
  const onAfterSubmit = returnTo
    ? () => {
        toast.success('Task completed successfully!')
        router.history.push(returnTo)
      }
    : undefined
  const {
    data: task,
    isFetching,
    isPending,
    isError,
    error,
    refetch,
  } = useAssignTaskToUserQuery()
  const [isRefreshThrottled, setIsRefreshThrottled] = useState(false)
  const refreshThrottleTimerRef = useRef<NodeJS.Timeout | null>(null)

  // close sidebar on mount and open it on unmount
  useEffectOnce(() => {
    setOpen(false)

    return () => {
      setOpen(true)
      queryClient.removeQueries({
        queryKey: assignTaskToUserOptions().queryKey,
      })
    }
  })

  useEffectOnce(() => () => {
    if (refreshThrottleTimerRef.current) {
      clearTimeout(refreshThrottleTimerRef.current)
    }
  })

  const refreshTask = () => {
    if (isRefreshThrottled || isFetching) {
      return
    }

    refetch()
    setIsRefreshThrottled(true)
    refreshThrottleTimerRef.current = setTimeout(() => {
      setIsRefreshThrottled(false)
    }, REFRESH_COOLDOWN_MS)
  }

  const isNoTaskAvailable =
    isError && (error as { code?: string } | null)?.code === NOT_FOUND_ERROR

  // Keep the screen mounted while a throttled re-check is in flight so it does
  // not flash <Loading /> when the query momentarily leaves the error state.
  if (isNoTaskAvailable || (isRefreshThrottled && isFetching)) {
    return (
      <NoTaskAvailable
        onRefresh={refreshTask}
        isRefreshing={isFetching}
        isThrottled={isRefreshThrottled}
      />
    )
  }

  if (isFetching || isPending) {
    resetAllVideoAnnotationStores(true)

    return <Loading />
  }

  if (isError) {
    navigate({
      to: '/dashboard',
    })

    return null
  }

  let taskPage: React.ReactNode

  if (task.type === 'video_annotation') {
    taskPage = (
      <TaskVideoAnnotationPage
        task={task}
        isNewTaskModalEnable={!returnTo}
        onAfterSubmit={onAfterSubmit}
      />
    )
  } else if (hasSolution(task)) {
    taskPage = (
      <SolutionPage
        task={task}
        isNewTaskModalEnable={!returnTo}
        onAfterSubmit={onAfterSubmit}
      />
    )
  } else {
    taskPage = (
      <TaskBasicPage
        task={task}
        isNewTaskModalEnable={!returnTo}
        onAfterSubmit={onAfterSubmit}
      />
    )
  }

  return (
    <>
      {taskPage}
      <TaskAutoReleasedModal currentTaskId={task.id} />
    </>
  )
}

const DEFAULT_SEARCH = {
  name: null,
  returnTo: null,
} as const

const SEARCH_SCHEMA = z.object({
  name: fallback(z.string().nullable(), DEFAULT_SEARCH.name).default(
    DEFAULT_SEARCH.name,
  ),
  returnTo: fallback(z.string().nullable(), DEFAULT_SEARCH.returnTo).default(
    DEFAULT_SEARCH.returnTo,
  ),
})

export const Route = createFileRoute('/_auth/task')({
  beforeLoad: () => {
    // TODO: we should check if the user has access to the task
  },
  component: TaskPageContainer,
  loader: () => ({ breadcrumbTitle: 'Task' }),
  validateSearch: zodValidator(SEARCH_SCHEMA),
  search: {
    middlewares: [stripSearchParams(DEFAULT_SEARCH)],
  },
})
